import {Box, Group, Slider, Text} from '@mantine/core';
import {
  IconMaximize,
  IconMinimize,
  IconPlayerPause,
  IconPlayerPlay,
  IconVolume,
  IconVolumeOff,
} from '@tabler/icons-react';
import type {FC} from 'react';
import {ActionIcon} from 'src/design/atoms/ActionIcon';

/** Formats a duration as `m:ss`, or `h:mm:ss` once it reaches an hour. */
export function formatVideoTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/**
 * Presentational control bar for a media player: play/pause, mute + volume,
 * a backward-clamped seek bar, elapsed/total time, and fullscreen.
 * @registry Media player control bar (play/pause, volume, seek, fullscreen).
 */
export interface VideoControlBarProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  muted: boolean;
  onToggleMute: () => void;
  volume: number;
  onVolumeChange: (volume: number) => void;
  showVolumeSlider?: boolean;
  currentSeconds: number;
  durationSeconds: number;
  maxSeekableSeconds: number;
  showSeekBar?: boolean;
  onSeek: (seconds: number) => void;
  onFullscreen?: () => void;
  isFullscreen?: boolean;
}

export const VideoControlBar: FC<VideoControlBarProps> = ({
  isPlaying,
  onPlayPause,
  muted,
  onToggleMute,
  volume,
  onVolumeChange,
  showVolumeSlider = true,
  currentSeconds,
  durationSeconds,
  maxSeekableSeconds,
  showSeekBar = true,
  onSeek,
  onFullscreen,
  isFullscreen = false,
}) => {
  const durationUnknown = durationSeconds <= 0;
  const iconColor = isFullscreen ? 'white' : 'black';

  return (
    <Box
      px={isFullscreen ? 'md' : 0}
      py={isFullscreen ? 'xs' : 0}
      bg={isFullscreen ? 'dark.8' : undefined}>
      {showSeekBar && (
        <Slider
          w="100%"
          min={0}
          max={durationSeconds}
          value={Math.min(currentSeconds, durationSeconds)}
          disabled={durationUnknown}
          label={formatVideoTime}
          onChange={(value) => onSeek(Math.min(value, maxSeekableSeconds))}
          mb="xs"
        />
      )}
      <Group gap="sm" wrap="nowrap">
        <ActionIcon
          size="lg"
          color={iconColor}
          tooltipText={isPlaying ? 'Pause' : 'Play'}
          onClick={onPlayPause}>
          {isPlaying ? (
            <IconPlayerPause size={20} />
          ) : (
            <IconPlayerPlay size={20} />
          )}
        </ActionIcon>

        <Group gap="xs" wrap="nowrap" miw={0}>
          <ActionIcon
            size="lg"
            color={iconColor}
            tooltipText={muted ? 'Unmute' : 'Mute'}
            onClick={onToggleMute}>
            {muted ? <IconVolumeOff size={20} /> : <IconVolume size={20} />}
          </ActionIcon>
          {showVolumeSlider && (
            <Slider
              w={90}
              miw={40}
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={onVolumeChange}
              label={null}
            />
          )}
        </Group>

        <Text
          size="sm"
          c={isFullscreen ? 'gray.4' : 'dimmed'}
          style={{whiteSpace: 'nowrap'}}>
          {formatVideoTime(currentSeconds)} / {formatVideoTime(durationSeconds)}
        </Text>

        <Box style={{flexGrow: 1}} />

        {onFullscreen && (
          <ActionIcon
            size="lg"
            color={iconColor}
            tooltipText={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            onClick={onFullscreen}>
            {isFullscreen ? (
              <IconMinimize size={20} />
            ) : (
              <IconMaximize size={20} />
            )}
          </ActionIcon>
        )}
      </Group>
    </Box>
  );
};
